import React, {
  ComponentProps,
  RefObject,
  useState,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createContext } from "@wss/react";
import { cls } from "@wss/cls";

import("scrollyfills");

type Threshold = 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1;

/**
 * We call `preventDefault` on mouse down to prevent dragging elements when attempting to drag the carousel.
 * This interferes with selecting inputs and clicking buttons.
 * For those elements, and all elements listed below, we will not call `preventDefault`.
 */
const whitelistedElements = ["INPUT", "BUTTON", "SELECT", "TEXTAREA"];

/** Returns the first visible element */
function findFirstScrollToElement(
  carousel: HTMLUListElement,
  threshold: Threshold = 0.5, // Default to needing half the item visible to be shown
): Element | undefined {
  const { children } = carousel;
  const { left: carouselLeft } = carousel.getBoundingClientRect();
  for (const elem of children) {
    const { left, width } = elem.getBoundingClientRect();
    const offset = left - carouselLeft;
    if (offset + width > width * threshold) return elem;
  }
}

/** Returns the last visible element */
function findLastScrollToElement(
  carousel: HTMLUListElement,
  threshold: Threshold = 0.5, // Default to needing half the item visible to be shown
): Element | undefined {
  const { children } = carousel;
  const { left: carouselLeft, width: carouselWidth } =
    carousel.getBoundingClientRect();
  for (const elem of new Array(...children).reverse()) {
    const { left, width } = elem.getBoundingClientRect();
    const offset = left - carouselLeft;
    // Skip items passed the carousel
    if (Math.round(left) > Math.round(carouselLeft + carouselWidth)) continue;
    if (offset + width > width * threshold) return elem;
  }
}

function isLastElementVisible(
  carousel: HTMLUListElement,
  threshold: Threshold = 0, // Default to any amount of the item visible to be shown
): Element | undefined {
  const { children } = carousel;
  const lastChild = children.item(children.length - 1);
  if (!lastChild) return;
  const { width: carouselWidth, left: carouselLeft } =
    carousel.getBoundingClientRect();
  const { left, width } = lastChild.getBoundingClientRect();
  const offset = left + width * threshold - carouselLeft;
  const isVisible = offset < carouselWidth;
  if (isVisible) return lastChild;
  return undefined;
}

function getScrollOffset(carousel: HTMLUListElement, elem: Element): number {
  const { left } = elem.getBoundingClientRect();
  const { left: carouselLeft } = carousel.getBoundingClientRect();
  return left - carouselLeft + carousel.scrollLeft;
}

function scrollCarousel(
  carousel: HTMLUListElement,
  left: number,
): Promise<void> {
  return new Promise<void>((resolve) => {
    carousel.addEventListener("scrollend", () => resolve(), { once: true });
    carousel.scrollTo({ left, behavior });
  });
}

function scrollToEnd(carousel: HTMLUListElement): Promise<void> {
  return scrollCarousel(carousel, carousel.scrollWidth);
}

type CarouselContext = {
  carouselRef: RefObject<HTMLUListElement>;
  nextButtonRef: RefObject<HTMLButtonElement>;
  previousButtonRef: RefObject<HTMLButtonElement>;
  slide: (direction: "next" | "previous") => void;
  goTo: (index: number) => void;
  startAutoplay: () => void;
  stopAutoplay: () => void;
};

const [Context, useContext] = createContext<CarouselContext>();

const mediaQuery = "(prefers-reduced-motion: reduce)";
const isReduced = window.matchMedia?.(mediaQuery).matches === true;
const behavior = isReduced ? "auto" : "smooth";

type SuperSimpleCarouselScrollButtonProps = ComponentProps<"button"> & {
  threshold?: Threshold;
};

function SuperSimpleCarouselNext({
  className,
  children,
  onClick,
  threshold = 0.5,
  ...rest
}: SuperSimpleCarouselScrollButtonProps) {
  const { carouselRef, nextButtonRef, previousButtonRef, slide } = useContext();
  const [hidden, setHidden] = useState<boolean>(false);
  const shouldMoveFocus = useRef<boolean>(false);

  function slideNext(e: ReactMouseEvent<HTMLButtonElement>) {
    slide("next");
    onClick?.(e);
  }

  function setMoveFocus() {
    shouldMoveFocus.current = true;
  }

  const handleUpdateFocus = useCallback(
    function onUpdateFocus() {
      if (!previousButtonRef.current || !shouldMoveFocus.current) return;
      previousButtonRef.current.focus();
      shouldMoveFocus.current = false;
    },
    [previousButtonRef],
  );

  useEffect(
    function initHideNextButton() {
      if (!carouselRef.current) return;
      const carousel = carouselRef.current;
      function onObserve(entries: IntersectionObserverEntry[]) {
        if (!entries[0]) return;
        const isHidden = entries[0].intersectionRatio > threshold;
        if (isHidden) {
          carousel.addEventListener("scrollend", handleUpdateFocus, {
            once: true,
          });
        }
        setHidden(isHidden);
      }
      const observer = new IntersectionObserver(onObserve, {
        root: carousel,
        threshold: [0, threshold, 1],
      });
      const { children } = carousel;
      const lastChild = children[children.length - 1];
      if (!lastChild) return;
      observer.observe(lastChild);
      return function cleanupHideNextButton() {
        return observer.disconnect();
      };
    },
    [carouselRef, handleUpdateFocus, threshold],
  );

  return (
    <button
      ref={nextButtonRef}
      type="button"
      aria-label="next set of slides"
      {...rest}
      className={cls(
        "no-js absolute right-0 top-0 bottom-0 bg-clip-padding z-5",
        hidden ? "hidden" : className,
      )}
      onClick={slideNext}
      onFocus={setMoveFocus}
    >
      {children}
    </button>
  );
}

function SuperSimpleCarouselPrevious({
  className,
  children,
  onClick,
  threshold = 0.5,
  ...rest
}: SuperSimpleCarouselScrollButtonProps) {
  const { carouselRef, previousButtonRef, nextButtonRef, slide } = useContext();
  const [hidden, setHidden] = useState<boolean>(true);
  const shouldMoveFocus = useRef<boolean>(false);

  function slidePrevious(e: ReactMouseEvent<HTMLButtonElement>) {
    slide("previous");
    onClick?.(e);
  }

  function setMoveFocus() {
    shouldMoveFocus.current = true;
  }

  const handleUpdateFocus = useCallback(
    function onUpdateFocus() {
      if (!nextButtonRef.current || !shouldMoveFocus.current) return;
      nextButtonRef.current.focus();
      shouldMoveFocus.current = false;
    },
    [nextButtonRef],
  );

  useEffect(
    function initHidePreviousButton() {
      if (!carouselRef.current?.children[0]) return;
      const carousel = carouselRef.current;
      function onObserve(entries: IntersectionObserverEntry[]) {
        if (!entries[0]) return;
        const isHidden = entries[0].intersectionRatio > threshold;
        if (isHidden) {
          carousel.addEventListener("scrollend", handleUpdateFocus, {
            once: true,
          });
        }
        setHidden(isHidden);
      }
      const observer = new IntersectionObserver(onObserve, {
        root: carouselRef.current,
        threshold: [0.1, threshold, 1],
      });
      observer.observe(carouselRef.current.children[0]);
      return function cleanupHideNextButton() {
        return observer.disconnect();
      };
    },
    [carouselRef, handleUpdateFocus, threshold],
  );

  return (
    <button
      ref={previousButtonRef}
      type="button"
      aria-label="previous set of slides"
      {...rest}
      className={cls(
        "no-js absolute left-0 top-0 bottom-0 bg-clip-padding z-5",
        hidden ? "hidden" : className,
      )}
      onClick={slidePrevious}
      onFocus={setMoveFocus}
    >
      {children}
    </button>
  );
}

function SuperSimpleCarouselItem({
  children,
  className,
  ...rest
}: ComponentProps<"li">) {
  return (
    <li {...rest} className={cls("snap-start", className)}>
      {children}
    </li>
  );
}

type SuperSimpleCarouselContentProps = ComponentProps<"ul"> & {
  /** Determines at what point an element is visible.
   * @example 0.5 = '50%'
   */
  threshold?: Threshold;
};

function SuperSimpleCarouselContent({
  children,
  className,
  threshold,
  onMouseDown,
  ...rest
}: SuperSimpleCarouselContentProps) {
  const { carouselRef } = useContext();
  const [mouseDown, setMouseDown] = useState<boolean>(false);
  const position = useRef<number>(0);
  const controller = useRef<AbortController | null>(null);
  const isScrolling = useRef<boolean>(false);

  const handleMouseMove = useCallback(
    function handleMouseMove(e: MouseEvent) {
      if (!carouselRef.current) return;
      setMouseDown(true);
      const offset = position.current - e.screenX;
      const { scrollLeft } = carouselRef.current;
      const movement = scrollLeft + offset;
      if (Math.abs(offset) < 5) return;
      isScrolling.current = true;
      carouselRef.current.scrollLeft = movement;
      position.current = e.screenX;
    },
    [carouselRef],
  );

  const handleMouseUp = useCallback(
    async function handleMouseUp() {
      controller.current?.abort();
      controller.current = new AbortController();
      if (!carouselRef.current || !isScrolling.current) return;
      const carousel = carouselRef.current;
      const { scrollWidth, scrollLeft } = carousel;
      const { width } = carouselRef.current.getBoundingClientRect();
      const scrollOffset = Math.ceil(width + scrollLeft);
      if (scrollOffset < scrollWidth) {
        const scrollTo = findFirstScrollToElement(carousel, threshold);
        const lastChild = isLastElementVisible(carousel, threshold);
        if (lastChild) {
          // Deals with product cards that need a right border with the carousel border
          // Basically it eliminates the double border since scrolling the container won't show the negative margin
          await scrollToEnd(carousel);
        } else if (scrollTo) {
          const offset = getScrollOffset(carousel, scrollTo);
          const style =
            parseFloat(getComputedStyle(carousel).scrollPaddingLeft) || 0;
          await scrollCarousel(carousel, offset - style);
        }
      }
      setMouseDown(false);
      isScrolling.current = false;
    },
    [carouselRef, threshold],
  );

  const handleClick = useCallback(function onClick(e: ReactMouseEvent) {
    if (!isScrolling.current) return;
    e.preventDefault();
  }, []);

  const handleMouseDown = useCallback(
    function handleMouseDown(e: ReactMouseEvent<HTMLUListElement>) {
      onMouseDown?.(e);
      if (!carouselRef.current) return;
      if (!(e.target instanceof HTMLElement)) return;
      if (whitelistedElements.includes(e.target.nodeName)) return;
      if (!carouselRef.current.contains(e.target)) return;
      e.preventDefault();
      controller.current = new AbortController();
      const opts = { signal: controller.current.signal };
      carouselRef.current.addEventListener("mousemove", handleMouseMove, opts);
      carouselRef.current.addEventListener("mouseup", handleMouseUp, opts);
      carouselRef.current.addEventListener("mouseleave", handleMouseUp, opts);
      position.current = e.screenX;
    },
    [carouselRef, handleMouseMove, handleMouseUp, onMouseDown],
  );

  useEffect(
    function initDisableNonVisibleElements() {
      if (!carouselRef.current) return;
      function onObserve(entries: IntersectionObserverEntry[]) {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.removeAttribute("inert");
          } else {
            entry.target.setAttribute("inert", "");
          }
        }
      }
      const observer = new IntersectionObserver(onObserve, {
        root: carouselRef.current,
        threshold: [threshold ?? 0.5],
      });
      for (const child of carouselRef.current.children) {
        observer.observe(child);
      }

      return function cleanupDisableNonVisibleElements() {
        observer.disconnect();
      };
    },
    [carouselRef, threshold],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <ul
      {...rest}
      ref={carouselRef}
      className={cls(
        "flex no-scrollbar overflow-auto select-none list-none p-0",
        mouseDown ? "cursor-grabbing" : "cursor-grab",
        !mouseDown ? "snap-x snap-mandatory" : "snap-none",
        className,
      )}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {children}
    </ul>
  );
}

type UseCarouselArgs = {
  /** Enables the carousel to move an it's own.
   *
   * See @param step for details on how it moves. */
  autoplay?: boolean;
  /** The amount of time between auto scrolls in milliseconds. */
  duration?: number;
  /** The number of item that will be scrolled when using autoplay or the SuperSimpleCarousel.Previous and SuperSimpleCarousel.Next buttons.
   *
   * `undefined` will scroll the carousel it's full visible width.
   */
  step?: number;
};

function useCarousel(args?: UseCarouselArgs): CarouselContext {
  const carouselRef = useRef<HTMLUListElement>(null);
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoplay = args?.autoplay || false;
  const duration = args?.duration || 7000;
  const step = args?.step;

  const goTo = useCallback(function handleGoTo(index: number) {
    if (!carouselRef.current) return;
    const { children } = carouselRef.current;
    const child = children[index];
    if (!child) throw new Error(`Can not find children at ${index}`);
    const offset = getScrollOffset(carouselRef.current, child);
    scrollCarousel(carouselRef.current, offset);
  }, []);

  const slideForward = useCallback(function onSlideForward(
    carousel: HTMLUListElement,
    step: number,
  ) {
    const slideTo = findFirstScrollToElement(carousel);
    if (!slideTo) return;
    const lastChild = isLastElementVisible(carousel);
    // At the end, go to start
    if (slideTo === lastChild) return scrollCarousel(carousel, 0);
    const children = Array.from(carousel.children);
    const nextSlideTo = children.indexOf(slideTo) + step;
    // Go to end if we are going to scroll passed the end
    if (nextSlideTo > children.length - 1) return scrollToEnd(carousel);
    const nextScrollToElem = children[nextSlideTo];
    if (!nextScrollToElem) return;
    const offset = getScrollOffset(carousel, nextScrollToElem);
    return scrollCarousel(carousel, offset);
  }, []);

  const slideBack = useCallback(function onSlideForwards(
    carousel: HTMLUListElement,
    step: number,
  ) {
    const slideTo = findFirstScrollToElement(carousel);
    if (!slideTo) return;
    const children = Array.from(carousel.children);
    // At the start, go to end. This is impossible unless we have reverse autoplay
    if (slideTo === children[0]) return scrollToEnd(carousel);
    const prevSlideTo = children.indexOf(slideTo) - step;
    // Go to start if we are going to scroll passed the start. This is also impossible without reverse autoplay
    if (prevSlideTo < 0) return scrollCarousel(carousel, 0);
    const nextScrollToElem = children[prevSlideTo];
    if (!nextScrollToElem) return;
    const { left, width } = nextScrollToElem.getBoundingClientRect();
    const offset = carousel.scrollLeft + left - width;
    return scrollCarousel(carousel, offset);
  }, []);

  const slideCarouselWidth = useCallback(function onScrollCarouselWidth(
    direction: "next" | "previous",
    carousel: HTMLUListElement,
  ) {
    const { scrollLeft, offsetWidth } = carousel;
    const scrollNext = direction === "next";
    const left = scrollNext
      ? scrollLeft + offsetWidth
      : scrollLeft - offsetWidth;
    const lastVisible = findLastScrollToElement(carousel);
    if (!lastVisible) return scrollCarousel(carousel, left);
    const { width } = lastVisible.getBoundingClientRect();
    // Full width items 0 out, this moves them by their width
    if (left === Math.round(width)) return scrollCarousel(carousel, width);
    const offset = Math.round(scrollNext ? left - width : left + width);
    // Full width items match the current scrollLeft
    if (offset === Math.trunc(scrollLeft)) {
      const index = Array.from(carousel.children).indexOf(lastVisible);
      return scrollCarousel(carousel, width * index);
    }
    return scrollCarousel(carousel, offset);
  }, []);

  const slide = useCallback(
    function handleSlide(direction: "next" | "previous") {
      if (!carouselRef.current) return;
      if (step === undefined) {
        return slideCarouselWidth(direction, carouselRef.current);
      }
      if (direction === "next") return slideForward(carouselRef.current, step);
      return slideBack(carouselRef.current, step);
    },
    [slideBack, slideCarouselWidth, slideForward, step],
  );

  const handleAutoSlide = useCallback(
    function onAutoSlide() {
      if (!carouselRef.current?.children) return;
      const carousel = carouselRef.current;
      const { scrollLeft, scrollWidth } = carouselRef.current;
      const { width } = carousel.getBoundingClientRect();
      const isAtEnd = Math.ceil(width + scrollLeft) >= scrollWidth;
      if (isAtEnd) return scrollCarousel(carousel, 0);
      return slide("next");
    },
    [slide],
  );

  const startAutoplay = useCallback(
    function onStartAutoplay() {
      if (timeoutRef.current) return;
      const timeout = setInterval(handleAutoSlide, duration);
      timeoutRef.current = timeout;
      return timeout;
    },
    [duration, handleAutoSlide],
  );

  const stopAutoplay = useCallback(function onStopAutoplay() {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const visibilityChange = useCallback(
    function onVisibilityChange() {
      if (!autoplay) return;
      if (document.visibilityState === "hidden") return stopAutoplay();
      if (timeoutRef.current) return; // Autoplay already started
      return startAutoplay();
    },
    [autoplay, startAutoplay, stopAutoplay],
  );

  useEffect(
    function initAutoplay() {
      if (!autoplay) return;
      startAutoplay();
      return stopAutoplay;
    },
    [autoplay, duration, startAutoplay, stopAutoplay],
  );

  useEffect(
    function initAutoplayEventHandlers() {
      if (!carouselRef.current || !autoplay) return;

      const controller = new AbortController();
      const { signal } = controller;
      const carousel = carouselRef.current;
      carousel.addEventListener("focus", stopAutoplay, { signal });
      carousel.addEventListener("mouseenter", stopAutoplay, { signal });
      carousel.addEventListener("touchstart", stopAutoplay, { signal });
      carousel.addEventListener("blur", startAutoplay, { signal });
      carousel.addEventListener("touchend", startAutoplay, { signal });
      carousel.addEventListener("mouseleave", startAutoplay, { signal });
      document.addEventListener("visibilitychange", visibilityChange, {
        signal,
      });
      return function cleanupAutoplayEventHandlers() {
        controller.abort();
      };
    },
    [autoplay, startAutoplay, stopAutoplay, visibilityChange],
  );

  return useMemo(
    () => ({
      carouselRef,
      previousButtonRef,
      nextButtonRef,
      slide,
      goTo,
      startAutoplay,
      stopAutoplay,
    }),
    [goTo, slide, startAutoplay, stopAutoplay],
  );
}

type NeverUseCarouselArgs = {
  [Key in keyof UseCarouselArgs]?: never;
};

type WithCarousel = NeverUseCarouselArgs & {
  carousel: ReturnType<typeof useCarousel>;
};

type WithoutCarousel = UseCarouselArgs & {
  carousel?: never;
};

type SuperSimpleCarouselProps = ComponentProps<"div"> &
  (WithCarousel | WithoutCarousel);

/** @see {@link https://tfs.clarkinc.biz/DefaultCollection/_git/Webstaurant.StoreFront?path=/docs/Storefront%20Frontend/Carousels.md Documentation} */
export function SuperSimpleCarousel({
  autoplay,
  step,
  duration,
  carousel,
  className,
  children,
  ...rest
}: SuperSimpleCarouselProps) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hook = carousel ?? useCarousel({ autoplay, step, duration });

  return (
    <Context.Provider value={hook}>
      <div {...rest} className={cls("relative overflow-hidden", className)}>
        {children}
      </div>
    </Context.Provider>
  );
}

SuperSimpleCarousel.Content = SuperSimpleCarouselContent;
SuperSimpleCarousel.Item = SuperSimpleCarouselItem;
SuperSimpleCarousel.Previous = SuperSimpleCarouselPrevious;
SuperSimpleCarousel.Next = SuperSimpleCarouselNext;
SuperSimpleCarousel.useCarousel = useCarousel;
SuperSimpleCarousel.useContext = useContext;
